import { useMemo, useState } from "react";
import { CalendarHeart, Sparkles, Info } from "lucide-react";

function addDays(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d;
}

function fmt(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function diffDays(a, b) {
  const ms = a.getTime() - b.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

export default function CycleCalculator() {
  const [lastPeriod, setLastPeriod] = useState("");
  const [cycleLen, setCycleLen] = useState(28);
  const [periodLen, setPeriodLen] = useState(5);

  const results = useMemo(() => {
    if (!lastPeriod) return null;
    const start = new Date(lastPeriod + "T00:00:00");
    if (isNaN(start.getTime())) return null;
    const cycle = Math.max(20, Math.min(45, Number(cycleLen) || 28));
    const period = Math.max(2, Math.min(10, Number(periodLen) || 5));

    const nextPeriod = addDays(lastPeriod, cycle);
    const periodEnds = addDays(lastPeriod, period - 1);
    // Ovulation typically ~14 days before next period
    const ovulation = addDays(lastPeriod, cycle - 14);
    const fertileStart = addDays(lastPeriod, cycle - 19); // 5 days before ovulation
    const fertileEnd = addDays(lastPeriod, cycle - 13);   // 1 day after ovulation

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let cycleDay = diffDays(today, start) + 1;
    if (cycleDay < 1 || cycleDay > cycle) cycleDay = null;

    let phase = null;
    if (cycleDay) {
      if (cycleDay <= period) phase = "Menstrual";
      else if (cycleDay < cycle - 14) phase = "Follicular";
      else if (cycleDay <= cycle - 13) phase = "Ovulation";
      else phase = "Luteal";
    }

    return {
      nextPeriod,
      periodEnds,
      ovulation,
      fertileStart,
      fertileEnd,
      cycleDay,
      phase,
      cycle,
      period,
    };
  }, [lastPeriod, cycleLen, periodLen]);

  return (
    <section
      data-testid="cycle-calculator"
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-14"
    >
      <div className="bg-white rounded-3xl shadow-hover border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-faytex-pink to-faytex-pinkHover text-white px-6 md:px-10 py-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <CalendarHeart className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest opacity-90">Free tool</p>
            <h2 className="font-heading text-2xl md:text-3xl leading-tight">
              Your Personal Cycle Calculator
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-0">
          {/* Inputs */}
          <div className="md:col-span-2 p-6 md:p-10 bg-faytex-pinkLight/30 md:border-r border-faytex-pinkLight">
            <p className="text-xs uppercase tracking-widest text-faytex-pinkHover font-semibold">
              Tell us about your cycle
            </p>
            <div className="mt-5 space-y-5">
              <label className="block">
                <span className="text-sm font-medium text-faytex-ink">
                  First day of your last period
                </span>
                <input
                  data-testid="cycle-last-period"
                  type="date"
                  value={lastPeriod}
                  onChange={(e) => setLastPeriod(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-faytex-pink focus:outline-none focus:ring-2 focus:ring-faytex-pink/30 bg-white"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-faytex-ink">
                  Average cycle length
                  <span className="ml-2 text-faytex-muted font-normal">{cycleLen} days</span>
                </span>
                <input
                  data-testid="cycle-length"
                  type="range"
                  min="20"
                  max="45"
                  value={cycleLen}
                  onChange={(e) => setCycleLen(Number(e.target.value))}
                  className="mt-3 w-full accent-faytex-pink"
                />
                <div className="flex justify-between text-[10px] text-faytex-muted mt-1">
                  <span>20</span>
                  <span>28 (avg)</span>
                  <span>45</span>
                </div>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-faytex-ink">
                  Average period length
                  <span className="ml-2 text-faytex-muted font-normal">{periodLen} days</span>
                </span>
                <input
                  data-testid="cycle-period-length"
                  type="range"
                  min="2"
                  max="10"
                  value={periodLen}
                  onChange={(e) => setPeriodLen(Number(e.target.value))}
                  className="mt-3 w-full accent-faytex-pink"
                />
                <div className="flex justify-between text-[10px] text-faytex-muted mt-1">
                  <span>2</span>
                  <span>5 (avg)</span>
                  <span>10</span>
                </div>
              </label>
            </div>
          </div>

          {/* Results */}
          <div className="md:col-span-3 p-6 md:p-10">
            {!results ? (
              <div
                data-testid="cycle-empty-state"
                className="h-full min-h-[280px] flex flex-col items-center justify-center text-center text-faytex-muted"
              >
                <Sparkles className="w-8 h-8 text-faytex-pink/60" />
                <p className="mt-4 max-w-xs">
                  Enter the first day of your last period on the left to see your personalised cycle
                  forecast.
                </p>
              </div>
            ) : (
              <div data-testid="cycle-results" className="space-y-4">
                {results.cycleDay && (
                  <div className="rounded-2xl bg-faytex-pink text-white p-5 flex items-center justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-widest opacity-80">Today</p>
                      <p className="font-heading text-2xl mt-1">
                        Day {results.cycleDay} of {results.cycle}
                      </p>
                    </div>
                    <span className="rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-widest">
                      {results.phase} phase
                    </span>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <ResultCard
                    label="Next period starts"
                    value={fmt(results.nextPeriod)}
                    accent="pink"
                    testid="result-next-period"
                  />
                  <ResultCard
                    label="Current period ends"
                    value={fmt(results.periodEnds)}
                    accent="mint"
                    testid="result-period-ends"
                  />
                  <ResultCard
                    label="Estimated ovulation"
                    value={fmt(results.ovulation)}
                    accent="orange"
                    testid="result-ovulation"
                  />
                  <ResultCard
                    label="Fertile window"
                    value={`${fmt(results.fertileStart)} → ${fmt(results.fertileEnd)}`}
                    accent="purple"
                    testid="result-fertile-window"
                  />
                </div>
                <div className="flex items-start gap-3 rounded-2xl bg-gray-50 border border-gray-100 p-4 text-xs text-faytex-muted">
                  <Info className="w-4 h-4 text-faytex-pink flex-shrink-0 mt-0.5" />
                  <p>
                    These estimates are based on average cycle patterns and are for educational
                    purposes only. Every body is unique — for medical concerns or contraceptive
                    decisions, please consult a healthcare professional.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultCard({ label, value, accent, testid }) {
  const accents = {
    pink: "border-faytex-pink/30 bg-faytex-pinkLight/40 text-faytex-pinkHover",
    mint: "border-emerald-200 bg-emerald-50 text-emerald-700",
    orange: "border-orange-200 bg-orange-50 text-orange-700",
    purple: "border-purple-200 bg-purple-50 text-purple-700",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-4 ${accents[accent] || accents.pink}`}
    >
      <p className="text-[11px] uppercase tracking-widest font-semibold opacity-80">{label}</p>
      <p className="mt-1.5 font-heading text-lg text-faytex-ink">{value}</p>
    </div>
  );
}
